import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { PageViewport } from "pdfjs-dist";
import * as pdfjsLib from "pdfjs-dist";
import { RiAddLine, RiSubtractLine } from "react-icons/ri";
import "./OcrPdfViewer.styles.scss";
import { getLabelFromKey } from "../../helpers";

pdfjsLib.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjsLib.version}/build/pdf.worker.min.js`;

export interface OcrFoundFieldEntry {
  value?: string;
  coordinates?: Array<{
    page: number;
    bbox: Array<{ x: number; y: number }>;
  }>;
}

interface OcrPdfViewerProps {
  foundFields?: Record<string, OcrFoundFieldEntry>;
  highlightedUrl: string;
}

const FIELD_PRIORITY_ORDER = [
  "summary",
  "patient_name",
  "patient_age",
  "primary_drug",
  "primary_drug_dosage",
  "secondary_drug",
  "secondary_drug_dosage",
  "therapy_type",
  "therapy_indication",
  "cancer_type",
  "next_cycle",
] as const;

function entryHasValueKey(entry: unknown): entry is OcrFoundFieldEntry & { value: string } {
  return (
    !!entry &&
    typeof entry === "object" &&
    Object.prototype.hasOwnProperty.call(entry, "value")
  );
}

/** Non-empty bbox list on PDF (list item can jump + show overlay). */
function entryHasPdfCoordinates(entry: OcrFoundFieldEntry): boolean {
  return Array.isArray(entry.coordinates) && entry.coordinates.length > 0;
}

function sortFieldEntries(
  fields: Record<string, OcrFoundFieldEntry>
): [string, OcrFoundFieldEntry & { value: string }][] {
  const usable = Object.entries(fields).filter(([, v]) => entryHasValueKey(v)) as [
    string,
    OcrFoundFieldEntry & { value: string },
  ][];
  const keys = new Set(usable.map(([k]) => k));

  const priority: [string, OcrFoundFieldEntry & { value: string }][] = [];
  for (const k of FIELD_PRIORITY_ORDER) {
    if (keys.has(k)) {
      priority.push([k, fields[k] as OcrFoundFieldEntry & { value: string }]);
      keys.delete(k);
    }
  }

  const rest: [string, OcrFoundFieldEntry & { value: string }][] = Array.from(keys)
    .sort((a, b) => a.localeCompare(b))
    .map((k) => [k, fields[k] as OcrFoundFieldEntry & { value: string }]);

  return [...priority, ...rest];
}

function normalizePdfPageNumber(page: number): number {
  if (page < 1) {
    return page + 1;
  }
  return page;
}

function fileNameFromUrl(url: string): string {
  try {
    const u = new URL(url);
    const parts = u.pathname.split("/").filter(Boolean);
    const last = parts[parts.length - 1];
    return last ? decodeURIComponent(last.split("?")[0]) : url;
  } catch {
    const q = url.split("?")[0];
    const seg = q.split("/").filter(Boolean).pop();
    return seg ? decodeURIComponent(seg) : url;
  }
}

/** Same blob/path with a new SAS token should not reset zoom / selection. */
function documentIdentityKey(url: string): string {
  try {
    const u = new URL(url);
    return `${u.origin}${u.pathname}`;
  } catch {
    return url.split("?")[0] || url;
  }
}

function minBboxX(coord: { bbox: Array<{ x: number; y: number }> }): number {
  if (!coord.bbox.length) {
    return 0;
  }
  return Math.min(...coord.bbox.map((p) => p.x));
}

/** Earliest page, then leftmost box on that page (matches “left side first”). */
function pickFocusPageFromCoordinates(
  coordinates: Array<{ page: number; bbox: Array<{ x: number; y: number }> }>
): number | null {
  if (!coordinates?.length) {
    return null;
  }
  const sorted = [...coordinates].sort((a, b) => {
    const pa = normalizePdfPageNumber(a.page);
    const pb = normalizePdfPageNumber(b.page);
    if (pa !== pb) {
      return pa - pb;
    }
    return minBboxX(a) - minBboxX(b);
  });
  return normalizePdfPageNumber(sorted[0].page);
}

function scrollContainerToCenterElement(
  container: HTMLElement,
  target: HTMLElement
) {
  const tRect = target.getBoundingClientRect();
  const cRect = container.getBoundingClientRect();
  const scrollLeft =
    tRect.left - cRect.left + container.scrollLeft - (container.clientWidth - tRect.width) / 2;
  const scrollTop =
    tRect.top - cRect.top + container.scrollTop - (container.clientHeight - tRect.height) / 2;
  container.scrollTo({
    left: Math.max(0, scrollLeft),
    top: Math.max(0, scrollTop),
    behavior: "smooth",
  });
}

/** Minimum highlight size (CSS px) so tiny OCR boxes stay visible when zoomed out. */
const HIGHLIGHT_MIN_PX = 8;

/** Map OCR bbox to CSS % inside the rendered page (same size as viewport). */
function bboxToOverlayStyles(
  bbox: Array<{ x: number; y: number }>,
  viewport: PageViewport
): React.CSSProperties {
  if (!bbox.length) {
    return { display: "none" };
  }
  const xs = bbox.map((p) => p.x);
  const ys = bbox.map((p) => p.y);
  const maxCoord = Math.max(...xs, ...ys);
  const minX = Math.min(...xs);
  const maxX = Math.max(...xs);
  const minY = Math.min(...ys);
  const maxY = Math.max(...ys);

  if (maxCoord <= 1.01) {
    let w = maxX - minX;
    let h = maxY - minY;
    const cx = (minX + maxX) / 2;
    const cy = (minY + maxY) / 2;
    const minWX = HIGHLIGHT_MIN_PX / viewport.width;
    const minHY = HIGHLIGHT_MIN_PX / viewport.height;
    w = Math.max(w, minWX);
    h = Math.max(h, minHY);
    return {
      left: `${(cx - w / 2) * 100}%`,
      top: `${(cy - h / 2) * 100}%`,
      width: `${w * 100}%`,
      height: `${h * 100}%`,
    };
  }

  let minVX = Infinity;
  let minVY = Infinity;
  let maxVX = -Infinity;
  let maxVY = -Infinity;
  for (const p of bbox) {
    const [vx, vy] = viewport.convertToViewportPoint(p.x, p.y);
    minVX = Math.min(minVX, vx);
    minVY = Math.min(minVY, vy);
    maxVX = Math.max(maxVX, vx);
    maxVY = Math.max(maxVY, vy);
  }
  const W = viewport.width;
  const H = viewport.height;
  let wPx = maxVX - minVX;
  let hPx = maxVY - minVY;
  const cx = (minVX + maxVX) / 2;
  const cy = (minVY + maxVY) / 2;
  wPx = Math.max(wPx, HIGHLIGHT_MIN_PX);
  hPx = Math.max(hPx, HIGHLIGHT_MIN_PX);
  const minVX2 = cx - wPx / 2;
  const minVY2 = cy - hPx / 2;
  return {
    left: `${(minVX2 / W) * 100}%`,
    top: `${(minVY2 / H) * 100}%`,
    width: `${(wPx / W) * 100}%`,
    height: `${(hPx / H) * 100}%`,
  };
}

/** Base scale at 100% zoom (browser-style). */
const BASE_RENDER_SCALE = 1; // 1.35
const ZOOM_STEP = 10;
const ZOOM_MIN = 25;
const ZOOM_MAX = 400;
/** Zoom applied when selecting a field from the list so small text is readable. */
const FIELD_FOCUS_ZOOM_PERCENT = 200;

const INTERSECTION_THRESHOLDS = Array.from({ length: 21 }, (_, i) => i / 20);

const OcrPdfViewer: React.FC<OcrPdfViewerProps> = ({
  foundFields: foundFieldsProp,
  highlightedUrl: highlightedUrlProp,
}) => {
  const urlPropRef = useRef(highlightedUrlProp);
  const [stableUrl, setStableUrl] = useState(highlightedUrlProp);
  const fieldsJsonPropRef = useRef<string | null>(null);
  const [stableFields, setStableFields] = useState<Record<string, OcrFoundFieldEntry>>(() => ({
    ...(foundFieldsProp ?? {}),
  }));

  useEffect(() => {
    if (urlPropRef.current === highlightedUrlProp) {
      return;
    }
    urlPropRef.current = highlightedUrlProp;
    setStableUrl(highlightedUrlProp);
  }, [highlightedUrlProp]);

  useEffect(() => {
    let json = "{}";
    try {
      json = JSON.stringify(foundFieldsProp ?? {});
    } catch {
      setStableFields({ ...(foundFieldsProp ?? {}) });
      return;
    }
    if (fieldsJsonPropRef.current === json) {
      return;
    }
    fieldsJsonPropRef.current = json;
    setStableFields(foundFieldsProp ?? {});
  }, [foundFieldsProp]);

  const sortedFieldEntries = useMemo(
    () => sortFieldEntries(stableFields),
    [stableFields]
  );

  const [pdfDoc, setPdfDoc] = useState<pdfjsLib.PDFDocumentProxy | null>(null);
  const [pdfError, setPdfError] = useState<string | null>(null);
  const [renderTick, setRenderTick] = useState(0);
  const [activeFieldKey, setActiveFieldKey] = useState<string | null>(null);
  const [zoomPercent, setZoomPercent] = useState(100);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageDraft, setPageDraft] = useState("1");
  const [pageInputFocused, setPageInputFocused] = useState(false);

  const viewportsRef = useRef<Map<number, PageViewport>>(new Map());
  const pageWrapRefs = useRef<Map<number, HTMLDivElement>>(new Map());
  const canvasContainerRef = useRef<HTMLDivElement>(null);
  const intersectionRatiosRef = useRef<Map<number, number>>(new Map());
  /** Page to scroll to after PDF re-renders for field focus zoom (set on list tap). */
  const pendingFieldScrollPageRef = useRef<number | null>(null);
  /** Last loaded PDF path identity (without query); used to preserve zoom when only SAS rotates. */
  const stablePdfUrlKeyRef = useRef<string>("");

  useEffect(() => {
    if (!activeFieldKey) {
      return;
    }
    const entry = stableFields[activeFieldKey];
    if (!entryHasValueKey(entry) || !entryHasPdfCoordinates(entry)) {
      setActiveFieldKey(null);
      setZoomPercent(100);
      pendingFieldScrollPageRef.current = null;
    }
  }, [stableFields, activeFieldKey]);

  const numPages = pdfDoc?.numPages ?? 0;
  const displayName = fileNameFromUrl(stableUrl || "");

  useEffect(() => {
    if (!stableUrl) {
      stablePdfUrlKeyRef.current = "";
      setPdfDoc(null);
      setPdfError(null);
      viewportsRef.current.clear();
      setActiveFieldKey(null);
      pendingFieldScrollPageRef.current = null;
      return;
    }

    const docKey = documentIdentityKey(stableUrl);
    const isNewDocument = stablePdfUrlKeyRef.current !== docKey;

    let cancelled = false;
    setPdfError(null);

    if (isNewDocument) {
      stablePdfUrlKeyRef.current = docKey;
      setZoomPercent(100);
      setCurrentPage(1);
      setPageDraft("1");
      setActiveFieldKey(null);
      pendingFieldScrollPageRef.current = null;
    }

    pdfjsLib
      .getDocument({ url: stableUrl })
      .promise.then(
        (doc) => {
          if (!cancelled) {
            setPdfDoc(doc);
          }
        },
        (err: Error) => {
          if (!cancelled) {
            setPdfError(err.message || "Unable to load PDF");
            setPdfDoc(null);
          }
        }
      );

    return () => {
      cancelled = true;
    };
  }, [stableUrl]);

  useEffect(() => {
    if (!pageInputFocused) {
      setPageDraft(String(Math.min(Math.max(1, currentPage), Math.max(1, numPages))));
    }
  }, [currentPage, numPages, pageInputFocused]);

  const effectiveScale = (BASE_RENDER_SCALE * zoomPercent) / 100;

  useLayoutEffect(() => {
    if (!pdfDoc) {
      return;
    }

    let cancelled = false;
    viewportsRef.current.clear();

    const dpr =
      typeof window !== "undefined" ? Math.min(window.devicePixelRatio || 1, 3) : 1;

    const renderAll = async () => {
      for (let i = 1; i <= pdfDoc.numPages; i++) {
        if (cancelled) {
          return;
        }
        const page = await pdfDoc.getPage(i);
        const viewport = page.getViewport({ scale: effectiveScale });
        viewportsRef.current.set(i, viewport);

        const canvas = document.getElementById(
          `ocr-pdf-canvas-${i}`
        ) as HTMLCanvasElement | null;
        if (!canvas) {
          continue;
        }
        const ctx = canvas.getContext("2d");
        if (!ctx) {
          continue;
        }
        canvas.width = Math.floor(viewport.width * dpr);
        canvas.height = Math.floor(viewport.height * dpr);
        canvas.style.width = `${viewport.width}px`;
        canvas.style.height = `${viewport.height}px`;

        const transform = dpr !== 1 ? [dpr, 0, 0, dpr, 0, 0] : undefined;
        await page
          .render({
            canvasContext: ctx,
            viewport,
            ...(transform ? { transform } : {}),
          })
          .promise;
      }
      if (!cancelled) {
        setRenderTick((t) => t + 1);
      }
    };

    renderAll();

    return () => {
      cancelled = true;
    };
  }, [pdfDoc, effectiveScale]);

  useEffect(() => {
    const root = canvasContainerRef.current;
    if (!pdfDoc || numPages < 1 || !root) {
      return;
    }

    intersectionRatiosRef.current.clear();

    const updateFromObserved = () => {
      let bestPage = 1;
      let bestRatio = -1;
      intersectionRatiosRef.current.forEach((r, p) => {
        if (r > bestRatio) {
          bestRatio = r;
          bestPage = p;
        }
      });
      if (bestRatio <= 0) {
        return;
      }
      setCurrentPage((prev) => (prev !== bestPage ? bestPage : prev));
    };

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const pageAttr = entry.target.getAttribute("data-page");
          const page = pageAttr ? parseInt(pageAttr, 10) : NaN;
          if (!Number.isFinite(page)) {
            continue;
          }
          intersectionRatiosRef.current.set(page, entry.intersectionRatio);
        }
        updateFromObserved();
      },
      { root, rootMargin: "0px", threshold: INTERSECTION_THRESHOLDS }
    );

    for (let i = 1; i <= numPages; i++) {
      const el = pageWrapRefs.current.get(i);
      if (el) {
        observer.observe(el);
      }
    }

    const rafId = window.requestAnimationFrame(() => {
      root.dispatchEvent(new Event("scroll"));
    });

    return () => {
      window.cancelAnimationFrame(rafId);
      observer.disconnect();
    };
  }, [pdfDoc, numPages, renderTick, zoomPercent]);

  useEffect(() => {
    if (!pdfDoc || numPages < 1 || !activeFieldKey) {
      return;
    }
    const page = pendingFieldScrollPageRef.current;
    if (page == null) {
      return;
    }
    pendingFieldScrollPageRef.current = null;
    const pageNum = Math.max(1, Math.min(numPages, page));

    const run = () => {
      const container = canvasContainerRef.current;
      const wrap = pageWrapRefs.current.get(pageNum);
      if (!container || !wrap) {
        return;
      }

      const highlights = Array.from(
        wrap.querySelectorAll<HTMLElement>(".ocr-pdf-highlight")
      ).filter((el) => el.offsetParent !== null);

      let targetEl: HTMLElement = wrap;
      if (highlights.length > 0) {
        highlights.sort(
          (a, b) => a.getBoundingClientRect().left - b.getBoundingClientRect().left
        );
        targetEl = highlights[0]!;
      }
      scrollContainerToCenterElement(container, targetEl);
      setCurrentPage(pageNum);
    };

    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(run);
    });
  }, [renderTick, activeFieldKey, pdfDoc, numPages]);

  useEffect(() => {
    const el = canvasContainerRef.current;
    if (!el) {
      return;
    }
    const onWheel = (e: WheelEvent) => {
      if (!e.ctrlKey && !e.metaKey) {
        return;
      }
      e.preventDefault();
      const delta = e.deltaY > 0 ? -ZOOM_STEP : ZOOM_STEP;
      setZoomPercent((z) => Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, z + delta)));
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, []);

  const scrollToPage = useCallback(
    (pageNum: number) => {
      const n = Math.max(1, Math.min(numPages, pageNum));
      const el = pageWrapRefs.current.get(n);
      el?.scrollIntoView({
        behavior: "smooth",
        block: "start",
        inline: "nearest",
      });
      setCurrentPage(n);
    },
    [numPages]
  );

  const handleFieldListItemTap = (fieldKey: string) => {
    const field = stableFields[fieldKey];
    if (!field || !entryHasPdfCoordinates(field)) {
      return;
    }
    if (activeFieldKey === fieldKey) {
      setActiveFieldKey(null);
      setZoomPercent(100);
      pendingFieldScrollPageRef.current = null;
      return;
    }
    setActiveFieldKey(fieldKey);
    setZoomPercent(
      Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, FIELD_FOCUS_ZOOM_PERCENT))
    );
    pendingFieldScrollPageRef.current = field.coordinates?.length
      ? pickFocusPageFromCoordinates(field.coordinates)
      : null;
  };

  const zoomOut = () => {
    setZoomPercent((z) => Math.max(ZOOM_MIN, z - ZOOM_STEP));
  };

  const zoomIn = () => {
    setZoomPercent((z) => Math.min(ZOOM_MAX, z + ZOOM_STEP));
  };

  const commitPageInput = () => {
    const raw = parseInt(pageDraft.trim(), 10);
    const p = Number.isFinite(raw)
      ? Math.max(1, Math.min(numPages, raw))
      : currentPage;
    setPageDraft(String(p));
    scrollToPage(p);
    setPageInputFocused(false);
  };

  return (
    <div className="ocr-pdf-viewer-container">
      <div className="ocr-pdf-viewer-fields-container">
        <ul className="ocr-pdf-viewer-fields-list">
          {sortedFieldEntries.map(([key, value]) => {
            const hasCoords = entryHasPdfCoordinates(value);
            return (
              <li key={key}>
                <button
                  type="button"
                  disabled={!hasCoords}
                  className={
                    [
                      "ocr-pdf-viewer-field",
                      activeFieldKey === key ? "ocr-pdf-viewer-field--active" : "",
                      !hasCoords ? "ocr-pdf-viewer-field--no-coordinates" : "",
                    ]
                      .filter(Boolean)
                      .join(" ")
                  }
                  aria-disabled={!hasCoords}
                  title={
                    !hasCoords
                      ? "No PDF location available for this field"
                      : undefined
                  }
                  onClick={() => handleFieldListItemTap(key)}
                >
                  <div className="ocr-pdf-viewer-field-label">{getLabelFromKey(key)}</div>
                  <div className="ocr-pdf-viewer-field-value">{value.value}</div>
                </button>
              </li>
            );
          })}
        </ul>
      </div>
      <div className="ocr-pdf-viewer-pdf-column">
        <div ref={canvasContainerRef} className="ocr-pdf-viewer-canvas-container">
          {pdfError && (
            <div className="ocr-pdf-viewer-message ocr-pdf-viewer-message--error">
              {pdfError}
            </div>
          )}
          {!pdfError && !pdfDoc && stableUrl && (
            <div className="ocr-pdf-viewer-message">Loading PDF…</div>
          )}
          {pdfDoc &&
            numPages > 0 &&
            Array.from({ length: numPages }, (_, idx) => {
              const pageNum = idx + 1;
              const viewport = viewportsRef.current.get(pageNum);
              const fieldForHighlight =
                activeFieldKey && stableFields[activeFieldKey]
                  ? stableFields[activeFieldKey]
                  : null;
              const coordForPage = fieldForHighlight?.coordinates?.filter(
                (c) => normalizePdfPageNumber(c.page) === pageNum
              );

              return (
                <div
                  key={pageNum}
                  className="ocr-pdf-page-wrap"
                  data-page={pageNum}
                  ref={(el) => {
                    if (el) {
                      pageWrapRefs.current.set(pageNum, el);
                    } else {
                      pageWrapRefs.current.delete(pageNum);
                    }
                  }}
                >
                  <div className="ocr-pdf-page-inner">
                    <canvas
                      id={`ocr-pdf-canvas-${pageNum}`}
                      className="ocr-pdf-canvas"
                    />
                    {viewport &&
                      coordForPage?.map((coord, hi) => (
                        <div
                          key={`${pageNum}-${hi}`}
                          className="ocr-pdf-highlight"
                          style={bboxToOverlayStyles(coord.bbox, viewport)}
                        />
                      ))}
                  </div>
                </div>
              );
            })}
        </div>

        {pdfDoc && numPages > 0 && (
          <footer className="ocr-pdf-viewer-toolbar" aria-label="PDF controls">
            <div className="ocr-pdf-viewer-toolbar-filename" title={displayName}>
              {displayName}
            </div>
            <div className="ocr-pdf-viewer-toolbar-controls">
              <div className="ocr-pdf-viewer-toolbar-pagination">
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  className="ocr-pdf-viewer-toolbar-page-input"
                  aria-label="Current page"
                  value={pageInputFocused ? pageDraft : String(currentPage)}
                  onFocus={() => {
                    setPageInputFocused(true);
                    setPageDraft(String(currentPage));
                  }}
                  onChange={(e) => {
                    const v = e.target.value.replace(/\D/g, "");
                    setPageDraft(v);
                  }}
                  onBlur={() => commitPageInput()}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      (e.target as HTMLInputElement).blur();
                    }
                  }}
                />
                <span className="ocr-pdf-viewer-toolbar-page-sep">/</span>
                <span className="ocr-pdf-viewer-toolbar-page-total">{numPages}</span>
              </div>
              <div className="ocr-pdf-viewer-toolbar-divider" role="presentation" />
              <div className="ocr-pdf-viewer-toolbar-zoom">
                <button
                  type="button"
                  className="ocr-pdf-viewer-toolbar-icon-btn"
                  aria-label="Zoom out"
                  onClick={zoomOut}
                  disabled={zoomPercent <= ZOOM_MIN}
                >
                  <RiSubtractLine size={18} />
                </button>
                <span className="ocr-pdf-viewer-toolbar-zoom-value">{zoomPercent}%</span>
                <button
                  type="button"
                  className="ocr-pdf-viewer-toolbar-icon-btn"
                  aria-label="Zoom in"
                  onClick={zoomIn}
                  disabled={zoomPercent >= ZOOM_MAX}
                >
                  <RiAddLine size={18} />
                </button>
              </div>
            </div>
          </footer>
        )}
      </div>
    </div>
  );
};

export default OcrPdfViewer;
